import * as THREE from "three";

export function computeCurvatureDirections(sourceGeometry) {
  const geometry = sourceGeometry.clone();

  if (!geometry.attributes.normal) {
    geometry.computeVertexNormals();
  }

  const position = geometry.attributes.position;

  const normal = geometry.attributes.normal;

  const vertexCount = position.count;

  /*
   * --------------------------------------------------
   * SPATIAL WELDING
   * --------------------------------------------------
   *
   * Do NOT modify the actual render geometry.
   *
   * Instead, group vertices occupying approximately
   * the same position. This lets curvature information
   * cross UV seams and other duplicated vertices.
   */

  const WELD_EPSILON = 1e-5;

  function positionKey(x, y, z) {
    return [
      Math.round(x / WELD_EPSILON),

      Math.round(y / WELD_EPSILON),

      Math.round(z / WELD_EPSILON),
    ].join(",");
  }

  const vertexToWeld = new Int32Array(vertexCount);

  const weldMap = new Map();

  const weldGroups = [];

  const tempPosition = new THREE.Vector3();

  for (let i = 0; i < vertexCount; i++) {
    tempPosition.fromBufferAttribute(position, i);

    const key = positionKey(tempPosition.x, tempPosition.y, tempPosition.z);

    let weldIndex = weldMap.get(key);

    if (weldIndex === undefined) {
      weldIndex = weldGroups.length;

      weldMap.set(key, weldIndex);

      weldGroups.push({
        vertices: [],
        position: tempPosition.clone(),
      });
    }

    vertexToWeld[i] = weldIndex;

    weldGroups[weldIndex].vertices.push(i);
  }

  const weldCount = weldGroups.length;

  /*
   * Average normals for each welded point.
   *
   * This gives the curvature solver one geometric
   * normal even when the source mesh duplicated
   * vertices because of UV seams.
   */
  const weldNormals = Array.from(
    {
      length: weldCount,
    },

    () => new THREE.Vector3(),
  );

  const tempNormal = new THREE.Vector3();

  for (let i = 0; i < vertexCount; i++) {
    tempNormal.fromBufferAttribute(normal, i).normalize();

    weldNormals[vertexToWeld[i]].add(tempNormal);
  }

  for (let i = 0; i < weldCount; i++) {
    if (weldNormals[i].lengthSq() > 1e-10) {
      weldNormals[i].normalize();
    }
  }

  /*
   * --------------------------------------------------
   * WELDED ADJACENCY
   * --------------------------------------------------
   */

  const neighbors = Array.from(
    {
      length: weldCount,
    },

    () => new Set(),
  );

  function connect(a, b) {
    if (a === b) {
      return;
    }

    neighbors[a].add(b);

    neighbors[b].add(a);
  }

  function addTriangle(a, b, c) {
    const wa = vertexToWeld[a];

    const wb = vertexToWeld[b];

    const wc = vertexToWeld[c];

    connect(wa, wb);

    connect(wb, wc);

    connect(wc, wa);
  }

  if (geometry.index) {
    const index = geometry.index.array;

    for (let i = 0; i < index.length; i += 3) {
      addTriangle(index[i], index[i + 1], index[i + 2]);
    }
  } else {
    for (let i = 0; i < vertexCount; i += 3) {
      addTriangle(i, i + 1, i + 2);
    }
  }

  /*
   * --------------------------------------------------
   * INITIAL CURVATURE ORIENTATION
   * --------------------------------------------------
   *
   * Build a sign-independent orientation tensor
   * from every neighboring welded point.
   */

  let directions = new Array(weldCount);

  const edge = new THREE.Vector3();

  const tangent = new THREE.Vector3();

  const normalDelta = new THREE.Vector3();

  const fallbackAxis = new THREE.Vector3();

  const fallbackDirection = new THREE.Vector3();

  for (let i = 0; i < weldCount; i++) {
    const p = weldGroups[i].position;

    const n = weldNormals[i];

    let xx = 0;
    let xy = 0;
    let xz = 0;

    let yy = 0;
    let yz = 0;

    let zz = 0;

    for (const j of neighbors[i]) {
      const q = weldGroups[j].position;

      edge.subVectors(q, p);

      const edgeLength = edge.length();

      if (edgeLength < 1e-6) {
        continue;
      }

      tangent.copy(edge).addScaledVector(n, -edge.dot(n));

      const tangentLength = tangent.length();

      if (tangentLength < 1e-6) {
        continue;
      }

      tangent.divideScalar(tangentLength);

      normalDelta.subVectors(weldNormals[j], n);

      const curvature = normalDelta.length() / edgeLength;

      const weight = curvature * curvature;

      const x = tangent.x;

      const y = tangent.y;

      const z = tangent.z;

      xx += weight * x * x;

      xy += weight * x * y;

      xz += weight * x * z;

      yy += weight * y * y;

      yz += weight * y * z;

      zz += weight * z * z;
    }

    /*
     * Deterministic tangent-plane seed.
     */
    if (Math.abs(n.y) < 0.9) {
      fallbackAxis.set(0, 1, 0);
    } else {
      fallbackAxis.set(1, 0, 0);
    }

    fallbackDirection.crossVectors(fallbackAxis, n).normalize();

    const direction = fallbackDirection.clone();

    /*
     * Dominant eigenvector of the orientation
     * tensor using power iteration.
     */
    for (let iteration = 0; iteration < 8; iteration++) {
      const x = direction.x;

      const y = direction.y;

      const z = direction.z;

      direction.set(
        xx * x + xy * y + xz * z,

        xy * x + yy * y + yz * z,

        xz * x + yz * y + zz * z,
      );

      direction.addScaledVector(n, -direction.dot(n));

      if (direction.lengthSq() < 1e-10) {
        direction.copy(fallbackDirection);

        break;
      }

      direction.normalize();
    }

    directions[i] = direction;
  }

  /*
   * --------------------------------------------------
   * SIGN-AWARE FIELD SMOOTHING
   * --------------------------------------------------
   */

  const SMOOTHING_PASSES = 16;

  const SELF_WEIGHT = 2.0;

  for (let pass = 0; pass < SMOOTHING_PASSES; pass++) {
    const nextDirections = new Array(weldCount);

    for (let i = 0; i < weldCount; i++) {
      const n = weldNormals[i];

      const current = directions[i];

      const sum = current.clone().multiplyScalar(SELF_WEIGHT);

      let totalWeight = SELF_WEIGHT;

      for (const j of neighbors[i]) {
        const projected = directions[j].clone();

        /*
         * Parallel transport approximation:
         * project the neighboring orientation
         * into this vertex's tangent plane.
         */
        projected.addScaledVector(n, -projected.dot(n));

        if (projected.lengthSq() < 1e-10) {
          continue;
        }

        projected.normalize();

        /*
         * A line field has no arrow direction.
         * Flip equivalent vectors before averaging.
         */
        if (projected.dot(current) < 0) {
          projected.negate();
        }

        sum.add(projected);

        totalWeight += 1.0;
      }

      sum.divideScalar(totalWeight);

      sum.addScaledVector(n, -sum.dot(n));

      if (sum.lengthSq() < 1e-10) {
        sum.copy(current);
      } else {
        sum.normalize();
      }

      nextDirections[i] = sum;
    }

    directions = nextDirections;
  }

  /*
   * --------------------------------------------------
   * GLOBAL SIGN PROPAGATION
   * --------------------------------------------------
   *
   * Choose consistent vector signs across each
   * connected component.
   */

  const visited = new Uint8Array(weldCount);

  for (let seed = 0; seed < weldCount; seed++) {
    if (visited[seed]) {
      continue;
    }

    visited[seed] = 1;

    const queue = [seed];

    while (queue.length > 0) {
      const i = queue.pop();

      const current = directions[i];

      for (const j of neighbors[i]) {
        if (visited[j]) {
          continue;
        }

        const projected = current.clone();

        projected.addScaledVector(
          weldNormals[j],
          -projected.dot(weldNormals[j]),
        );

        if (projected.lengthSq() > 1e-10) {
          projected.normalize();

          if (projected.dot(directions[j]) < 0) {
            directions[j].negate();
          }
        }

        visited[j] = 1;

        queue.push(j);
      }
    }
  }

  /*
   * --------------------------------------------------
   * COPY WELDED FIELD BACK TO RENDER VERTICES
   * --------------------------------------------------
   *
   * Every duplicate render vertex at the same
   * position receives the same hatch orientation.
   */

  const output = new Float32Array(vertexCount * 3);

  for (let i = 0; i < vertexCount; i++) {
    const direction = directions[vertexToWeld[i]];

    const offset = i * 3;

    output[offset] = direction.x;

    output[offset + 1] = direction.y;

    output[offset + 2] = direction.z;
  }

  geometry.setAttribute("hatchDirection", new THREE.BufferAttribute(output, 3));

  return geometry;
}
